import { useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useTags } from 'tags/api'
import { Banner, Button, Modal, Spinner } from 'src/components/ui'
import { FetchPrescriptiveModel, TagDto } from 'src/services'
import { OutputTagTable, TargetValueInput } from 'pages/site/models/create'
import { useSite } from 'src/contexts/site'
import { useUpdateModelMutation } from 'pages/site/models/api'

type Props = {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  model: FetchPrescriptiveModel
}

export function ChangeTargetTagModal({
  isOpen,
  setIsOpen,
  model,
}: Props): JSX.Element {
  return (
    <Modal
      contentClassName="min-w-[min(1024px,90vw)]"
      isOpen={isOpen}
      title="Change Target Tag"
      close={() => setIsOpen(false)}
    >
      <ChangeTargetTagModalContent setIsOpen={setIsOpen} model={model} />
    </Modal>
  )
}

type ChangeTargetTagModalContentProps = {
  setIsOpen: (isOpen: boolean) => void
  model: FetchPrescriptiveModel
}

function ChangeTargetTagModalContent({
  setIsOpen,
  model,
}: ChangeTargetTagModalContentProps): JSX.Element {
  const tagsQuery = useTags()

  if (tagsQuery.isLoading) {
    return <Spinner />
  }

  if (tagsQuery.isError) {
    return (
      <Banner
        variant="error"
        className="my-s"
        rightComponent={
          <Button
            variant="primary"
            onClick={() => tagsQuery.refetch()}
            title="Try again"
          />
        }
      >
        Failed to load tags
      </Banner>
    )
  }

  return (
    <ChangeTargetTagModalContentWithData
      setIsOpen={setIsOpen}
      tags={tagsQuery.data}
      model={model}
    />
  )
}

type ChangeTargetTagModalContentWithDataProps = {
  setIsOpen: (isOpen: boolean) => void
  tags: TagDto[]
  model: FetchPrescriptiveModel
}

function ChangeTargetTagModalContentWithData({
  setIsOpen,
  tags,
  model,
}: ChangeTargetTagModalContentWithDataProps): JSX.Element {
  const [outputTagId, setOutputTagId] = useState<string | null>(model.tag.id)
  const [targetValue, setTargetValue] = useState<number>(model.targetValue)
  const { id } = useSite()
  const updateModelMutation = useUpdateModelMutation(model)

  const outputTag = useMemo(
    () => tags.find(t => t.tagNodeId === outputTagId),
    [tags, outputTagId],
  )

  function setOutputTag(tagName: string | null): void {
    if (tagName === null) {
      setOutputTagId(null)
      return
    }
    const tag = tags.find(t => t.tagName === tagName)
    if (tag) {
      setOutputTagId(tag.tagNodeId)
    }
  }

  async function handleConfirm(): Promise<void> {
    if (!outputTagId || targetValue === undefined) return
    await updateModelMutation.mutateAsync(
      {
        tagId: outputTagId,
        targetValue,
      },
      {
        onSuccess: () => {
          setIsOpen(false)
          toast.success('Model updated successfully', { position: 'top-right' })
        },
      },
    )
  }

  return (
    <div className="flex flex-col gap-s pt-s">
      <OutputTagTable
        factory={id}
        modelType="PrescriptiveModel"
        tags={tags}
        outputTag={outputTag?.tagName}
        setOutputTag={setOutputTag}
      />
      {outputTag && (
        <>
          <TargetValueInput
            tag={outputTag}
            onChange={setTargetValue}
            initialValue={
              model.tag.id === outputTagId ? model.targetValue : undefined
            }
            disableAutoValueUpdate={model.tag.id === outputTagId}
          />
          <div className="flex items-center justify-end gap-s">
            <Button
              title="Cancel"
              variant="tertiary"
              disabled={updateModelMutation.isLoading}
              onClick={() => setIsOpen(false)}
            />

            <Button
              title="Confirm"
              variant="primary"
              disabled={
                updateModelMutation.isLoading ||
                !outputTagId ||
                (outputTagId === model.tag.id &&
                  model.targetValue === targetValue)
              }
              onClick={handleConfirm}
            />
          </div>
        </>
      )}
    </div>
  )
}
